import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, LogIn, ShieldCheck } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import PartnerAuthShell from "@/components/partners/PartnerAuthShell";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { getCsrfToken } from "@/integrations/php/client";

const schema = z.object({
  email: z.string().email("Συμπληρώστε ένα έγκυρο email."),
  password: z.string().min(1, "Συμπληρώστε τον κωδικό σας."),
});

const PartnerLogin = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { email: "", password: "" } });

  const submit = async (values: z.infer<typeof schema>) => {
    setLoading(true);
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch("/api/partner-login.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify(values),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.success) throw new Error("invalid-login");
      sessionStorage.setItem("csrf_token", body.csrf_token);
      sessionStorage.setItem("partnerId", body.partner_id);
      sessionStorage.setItem("partnerName", body.partner_name);
      const next = searchParams.get("next");
      navigate(next?.startsWith("/") ? next : `/partner/${body.partner_id}/pipeline`);
    } catch (error) {
      toast({ title: "Δεν έγινε η σύνδεση", description: "Ελέγξτε το email και τον κωδικό σας και δοκιμάστε ξανά.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PartnerAuthShell>
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
        <div><p className="text-xs font-bold uppercase tracking-[0.12em] text-sky-700">Καλώς ήρθατε</p><h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950">Σύνδεση συνεργάτη</h1><p className="mt-2 text-sm leading-6 text-slate-600">Χρησιμοποιήστε το email και τον κωδικό του λογαριασμού σας.</p></div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="mt-6 space-y-4">
            <FormField control={form.control} name="email" render={({ field }) => <FormItem><FormLabel>Email</FormLabel><FormControl><Input type="email" autoComplete="email" inputMode="email" placeholder="name@example.gr" className="h-12 rounded-xl bg-slate-50 text-base sm:text-sm" {...field} /></FormControl><FormMessage /></FormItem>} />
            <FormField control={form.control} name="password" render={({ field }) => <FormItem><div className="flex items-center justify-between"><FormLabel>Κωδικός</FormLabel><Link to="/partner/password-reset" className="text-xs font-semibold text-sky-700 hover:underline">Ξέχασα τον κωδικό</Link></div><FormControl><div className="relative"><Input type={showPassword ? "text" : "password"} autoComplete="current-password" placeholder="Ο κωδικός σας" className="h-12 rounded-xl bg-slate-50 pr-12 text-base sm:text-sm" {...field} /><button type="button" onClick={() => setShowPassword((current) => !current)} className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center rounded-lg text-slate-500" aria-label={showPassword ? "Απόκρυψη κωδικού" : "Εμφάνιση κωδικού"}>{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></FormControl><FormMessage /></FormItem>} />
            <Button type="submit" disabled={loading} className="h-12 w-full rounded-xl bg-sky-600 text-base font-bold hover:bg-sky-700">{loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <LogIn className="mr-2 h-5 w-5" />} Σύνδεση</Button>
          </form>
        </Form>
        <div className="mt-6 border-t border-slate-100 pt-5 text-center"><Link to="/partner/password-reset" className="text-sm font-semibold text-sky-700 hover:underline">Πρώτη σύνδεση ή δημιουργία κωδικού</Link><p className="mt-3 flex items-center justify-center gap-2 text-xs leading-5 text-slate-500"><ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" /> Τα στοιχεία σας προστατεύονται με ασφαλή σύνδεση.</p></div>
      </div>
    </PartnerAuthShell>
  );
};

export default PartnerLogin;
